import { Avatar, Box, Button, Chip, Divider, IconButton, Tooltip, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import AlarmIcon from "@mui/icons-material/Alarm";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { ClientMessageModel } from "../../Models/Models";
import { Vocabulary } from "../../Utils/Vocabulary";
import { localUrlEnum } from "../../Utils/urlEnum";
import { clientMessagesType } from "../../Utils/autocompleteEnum";
import styles from "./LeadHeader.module.css";

const TYPE_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  [clientMessagesType.financingRequest]: { label: Vocabulary.financingRequest, color: "#1d4ed8", bg: "#eff6ff" },
  [clientMessagesType.vinRequest]:       { label: Vocabulary.vinRequest,        color: "#7c3aed", bg: "#f5f3ff" },
  [clientMessagesType.contactMessage]:   { label: "lead",                       color: "#FF6B1A", bg: "#fff7ed" },
};

interface LeadHeaderProps {
  lead: ClientMessageModel;
  pendingFollowUps: any[];
  onConvert: () => void;
}

export default function LeadHeader({ lead, pendingFollowUps, onConvert }: LeadHeaderProps) {
  const navigate = useNavigate();

  const client = lead.client;
  const displayName = client
    ? [client.lastName || client.last_name, client.firstName || client.first_name].filter(Boolean).join(" ") || lead.name
    : lead.name;
  const displayPhone = client?.phone || lead.phone;
  const displayEmail = client?.email || lead.email;

  const overdue = pendingFollowUps.filter((f) => moment(f.follow_up_date).isBefore(moment()));
  const isUrgent = overdue.length > 0;
  const next = [...pendingFollowUps]
    .filter((f) => !moment(f.follow_up_date).isBefore(moment()))
    .sort((a, b) => moment(a.follow_up_date).diff(moment(b.follow_up_date)))[0];

  return (
    <Box className={styles.header}>
      <Box className={styles.row}>

        {/* Back */}
        <Tooltip title={Vocabulary.backToLeads}>
          <IconButton
            onClick={() => navigate(localUrlEnum.clientMessages)}
            size="small"
            className={styles.backButton}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* Avatar */}
        <Avatar className={styles.avatar}>
          {(displayName || "?").split(" ").filter(Boolean).slice(0, 2).map((w) => w.charAt(0).toUpperCase()).join("")}
        </Avatar>

        {/* Identity */}
        <Box className={styles.identity}>
          <Box className={styles.nameRow}>
            <Typography variant="h6" className={styles.name}>
              {displayName || "—"}
            </Typography>
            {lead.type && TYPE_CONFIG[lead.type] && (
              <Chip
                label={TYPE_CONFIG[lead.type].label}
                size="small"
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  height: 24,
                  borderRadius: "6px",
                  color: TYPE_CONFIG[lead.type].color,
                  bgcolor: TYPE_CONFIG[lead.type].bg,
                  border: `1px solid ${TYPE_CONFIG[lead.type].color}40`,
                  flexShrink: 0,
                }}
              />
            )}
          </Box>

          <Box className={styles.metaRow}>
            {[
              displayPhone,
              displayEmail,
              [lead.brand, lead.model].filter(Boolean).join(" ") || null,
              lead.created_at ? moment.utc(lead.created_at).local().fromNow() : null,
            ]
              .filter(Boolean)
              .map((item, i, arr) => (
                <span key={i} className={styles.metaInline}>
                  <Typography variant="caption" className={styles.metaText}>
                    {item}
                  </Typography>
                  {i < arr.length - 1 && (
                    <Typography variant="caption" className={styles.metaSep}>•</Typography>
                  )}
                </span>
              ))}
          </Box>
        </Box>

        {/* Right: quick contact actions + Convert CTA */}
        <Box className={styles.actions}>
          {displayPhone && (
            <Tooltip title={Vocabulary.phone}>
              <IconButton
                component="a"
                href={`tel:${displayPhone}`}
                size="small"
                className={`${styles.quickActionBtn} ${styles.quickActionPhone}`}
              >
                <PhoneIcon style={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          {displayPhone && (
            <Tooltip title="WhatsApp">
              <IconButton
                component="a"
                href={`https://wa.me/${displayPhone.replace(/\D/g, "")}`}
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                className={`${styles.quickActionBtn} ${styles.quickActionWhatsapp}`}
              >
                <WhatsAppIcon style={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          {displayEmail && (
            <Tooltip title="Email">
              <IconButton
                component="a"
                href={`mailto:${displayEmail}`}
                size="small"
                className={`${styles.quickActionBtn} ${styles.quickActionEmail}`}
              >
                <EmailIcon style={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}

          {(displayPhone || displayEmail) && (
            <Divider orientation="vertical" flexItem className={styles.actionDivider} />
          )}

          <Button
            variant="contained"
            size="small"
            startIcon={<PersonAddIcon style={{ fontSize: 15 }} />}
            onClick={onConvert}
            className={styles.convertCta}
          >
            {lead.client || lead.client_id ? Vocabulary.updateClient || "Actualizează client" : Vocabulary.convertToClient}
          </Button>
        </Box>
      </Box>

      {/* Follow-up banner */}
      {pendingFollowUps.length > 0 && (
        <Box
          sx={{
            mt: 1,
            px: 2,
            py: 1.25,
            borderRadius: 1.5,
            border: "1px solid",
            borderColor: isUrgent ? "#fecaca" : "#fed7aa",
            bgcolor: isUrgent ? "#fef2f2" : "#fff7ed",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
            <AlarmIcon sx={{ fontSize: 18, color: isUrgent ? "#dc2626" : "#ea580c", flexShrink: 0 }} />
            <Box sx={{ minWidth: 0 }}>
              <span style={{ fontWeight: 600, fontSize: 13, color: isUrgent ? "#7f1d1d" : "#7c2d12" }}>
                {isUrgent
                  ? `${overdue.length} follow-up${overdue.length > 1 ? "-uri depășite" : " depășit"}`
                  : `${pendingFollowUps.length} follow-up${pendingFollowUps.length > 1 ? "-uri programate" : " programat"}`}
              </span>
              {(next || overdue[0]) && (
                <span style={{ fontSize: 13, color: isUrgent ? "#b91c1c" : "#c2410c", display: "block", wordBreak: "break-word", marginTop: 2 }}>
                  — {moment((next || overdue[0]).follow_up_date).format("DD/MM/YYYY HH:mm")} - {(next || overdue[0]).note}
                </span>
              )}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}
